import { useMemo } from 'react';
import { DeviceTrackingLogTypeEnum, GroundTrackingNodeLog } from '@/api/apis/route';
import { DeviceTrackingValueEnum } from './consts';
import {
  getGroundNodeCompletedInfo,
  getGroundNodeETAInfo,
  getGroundNodeFlatInfo,
  getGroundNodeModifiedInfo,
  getLogString,
} from './utils';

type GroundTrackingLogProps = {
  log: GroundTrackingNodeLog;
};

export const GroundTrackingLog = ({ log }: GroundTrackingLogProps) => {
  const startCheckpointInfo = useMemo(() => {
    switch (log.$type) {
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeAddedLogModel:
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeRemovedLogModel:
        return getGroundNodeFlatInfo(log.firstCheckpoint);
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeCompletedLogModel:
        return getGroundNodeCompletedInfo(log.firstCheckpoint);
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeEtaModifiedLogModel:
        return getGroundNodeETAInfo(log.firstCheckpoint);
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeModifiedLogModel:
        return getGroundNodeModifiedInfo(log.firstCheckpoint);
      default:
        return [];
    }
  }, [log]);

  const endCheckpointInfo = useMemo(() => {
    switch (log.$type) {
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeAddedLogModel:
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeRemovedLogModel:
        return getGroundNodeFlatInfo(log.lastCheckpoint);
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeCompletedLogModel:
        return getGroundNodeCompletedInfo(log.lastCheckpoint);
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeEtaModifiedLogModel:
        return getGroundNodeETAInfo(log.lastCheckpoint);
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeModifiedLogModel:
        return getGroundNodeModifiedInfo(log.lastCheckpoint);
      default:
        return [];
    }
  }, [log]);

  const viaCheckpointsInfo = useMemo(() => {
    switch (log.$type) {
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeAddedLogModel:
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeRemovedLogModel:
        return log.viaCheckpoints.flatMap((c) => getGroundNodeFlatInfo(c));
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeCompletedLogModel:
        return log.viaCheckpoints.flatMap((c) => getGroundNodeCompletedInfo(c));
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeEtaModifiedLogModel:
        return log.viaCheckpoints.flatMap((c) => getGroundNodeETAInfo(c));
      case DeviceTrackingLogTypeEnum.GroundTrackingNodeModifiedLogModel:
        return log.viaCheckpoints.flatMap((c) => getGroundNodeModifiedInfo(c));
      default:
        return [];
    }
  }, [log]);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-s-r text-base-20">
        {getLogString(DeviceTrackingValueEnum.TripNumber, log.tripNumber, null)}
      </span>
      <div className="flex flex-col gap-1">
        <span className="text-s-m text-base-5">Start checkpoint:</span>
        {startCheckpointInfo.map((info) => (
          <span key={info} className="text-s-m text-base-5">
            {info}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-s-m text-base-5">Via checkpoints:</span>
        {viaCheckpointsInfo.map((info) => (
          <span key={info} className="text-s-m text-base-5">
            {info}
          </span>
        ))}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-s-m text-base-5">End checkpoint:</span>
        {endCheckpointInfo.map((info) => (
          <span key={info} className="text-s-m text-base-5">
            {info}
          </span>
        ))}
      </div>
    </div>
  );
};
